export type CookieType = 'HTTP Cookie' | 'localStorage';

export type CookieEntry = {
  name: string;
  description: string;
  duration: string;
  type: CookieType;
};

export type ProviderEntry = {
  name: string;
  domain: string;
  privacyUrl: string;
  cookies: CookieEntry[];
};

export type CookieCategoryId =
  | 'necessary'
  | 'preferences'
  | 'statistics'
  | 'marketing';

type CategoryEntry = {
  id: CookieCategoryId;
  providers: ProviderEntry[];
};

const COOKIE_REGISTRY: readonly CategoryEntry[] = [
  {
    id: 'necessary',
    providers: [
      {
        name: 'My Pharmacy',
        domain: 'www.mypharmacy.co.uk',
        privacyUrl: 'https://www.mypharmacy.co.uk/cookie-policy/',
        cookies: [
          {
            name: '_consent',
            description:
              'Records your cookie consent level (all, essential, or custom) so the site can respect your preferences on each visit.',
            duration: '1 year',
            type: 'HTTP Cookie',
          },
          {
            name: 'cookie_consent_preferences',
            description:
              'Stores your granular consent choices per category (preferences, analytics, advertising) so they are preserved across sessions.',
            duration: '1 year',
            type: 'localStorage',
          },
          {
            name: '_medusa_cart_id',
            description:
              'Identifies your shopping basket so items are preserved between page loads and visits.',
            duration: '7 days',
            type: 'HTTP Cookie',
          },
          {
            name: '_medusa_jwt',
            description:
              'Keeps you securely signed in to your account without requiring you to log in on every page.',
            duration: 'Session',
            type: 'HTTP Cookie',
          },
          {
            name: '_medusa_cache_id',
            description:
              'Ensures you receive your own up-to-date personalised data rather than a cached version intended for another user.',
            duration: 'Session',
            type: 'HTTP Cookie',
          },
          {
            name: 'newt_same_as_billing',
            description:
              'Holds your "billing address same as delivery" choice during checkout so your order is placed with the correct billing details.',
            duration: 'Persistent',
            type: 'localStorage',
          },
          {
            name: 'newt_save_shipping_address',
            description:
              'Holds your choice of whether to save your delivery address to your account when you place the order.',
            duration: 'Persistent',
            type: 'localStorage',
          },
        ],
      },
    ],
  },
  {
    id: 'preferences',
    providers: [
      {
        name: 'My Pharmacy',
        domain: 'www.mypharmacy.co.uk',
        privacyUrl: 'https://www.mypharmacy.co.uk/cookie-policy/',
        cookies: [
          {
            name: '_recent_searches',
            description:
              'Remembers your most recent search terms so they can be suggested next time you search.',
            duration: 'Persistent',
            type: 'localStorage',
          },
        ],
      },
    ],
  },
  {
    id: 'statistics',
    providers: [
      {
        name: 'Google Analytics',
        domain: 'www.google.com',
        privacyUrl: 'https://policies.google.com/privacy',
        cookies: [
          {
            name: '_ga',
            description:
              'Registers a unique ID that is used to generate statistical data on how the visitor uses the website.',
            duration: '2 years',
            type: 'HTTP Cookie',
          },
          {
            name: '_ga_*',
            description:
              'Used by Google Analytics to collect data on the number of times a user has visited the website as well as dates for the first and most recent visit.',
            duration: '2 years',
            type: 'HTTP Cookie',
          },
          {
            name: '_gid',
            description:
              'Registers a unique ID that is used to generate statistical data on how the visitor uses the website.',
            duration: '1 day',
            type: 'HTTP Cookie',
          },
          {
            name: '_gat_gtag_*',
            description: 'Used by Google Tag Manager to throttle request rate.',
            duration: '1 minute',
            type: 'HTTP Cookie',
          },
        ],
      },
      {
        name: 'PostHog',
        domain: 'posthog.com',
        privacyUrl: 'https://posthog.com/privacy',
        cookies: [
          {
            name: 'ph_*_posthog',
            description:
              'Used by PostHog to store a unique user identifier and session data for product analytics.',
            duration: '1 year',
            type: 'HTTP Cookie',
          },
        ],
      },
    ],
  },
  {
    id: 'marketing',
    providers: [
      {
        name: 'Braze',
        domain: 'braze.com',
        privacyUrl: 'https://www.braze.com/company/legal/privacy',
        cookies: [
          {
            name: 'ab.storage.deviceId.*',
            description:
              'Stores a unique identifier for the device used to send messages and track delivery.',
            duration: 'Persistent',
            type: 'localStorage',
          },
          {
            name: 'ab.storage.userId.*',
            description:
              'Stores the Braze user identifier to associate events and messages with the correct user profile.',
            duration: 'Persistent',
            type: 'localStorage',
          },
          {
            name: 'ab.storage.sessionId.*',
            description:
              'Stores the current session identifier used to track user activity within a single visit.',
            duration: 'Session',
            type: 'localStorage',
          },
          {
            name: 'ab.storage.sdkVersion.*',
            description:
              'Records the version of the Braze Web SDK in use to ensure compatibility with the dashboard.',
            duration: 'Persistent',
            type: 'localStorage',
          },
        ],
      },
      {
        name: 'Google Ads',
        domain: 'www.google.com',
        privacyUrl: 'https://policies.google.com/privacy',
        cookies: [
          {
            name: '_gcl_au',
            description:
              'Used by Google AdSense to experiment with advertisement efficiency across websites using their services.',
            duration: '3 months',
            type: 'HTTP Cookie',
          },
        ],
      },
    ],
  },
];

export const getCategoryProviders = (id: CookieCategoryId): ProviderEntry[] =>
  COOKIE_REGISTRY.find((c) => c.id === id)?.providers ?? [];
